import { useCallback, useEffect, useRef } from 'react'

import { ChatMessage } from '../chatMessage'

export interface UseChatAutoScrollOptions {
  messages: ChatMessage[]
  streamingText: string
  threshold?: number
}

/**
 * Keeps a scrollable message container following new content without
 * overriding the reader. A "pinned to bottom" flag is recomputed from the
 * `onScroll` handler — captured as the user scrolls, before new content
 * renders — so the container follows the bottom only when the user was
 * already there. Committed messages (`messages`) and streaming text
 * (`streamingText`) both honor that flag, except the user's own sent message,
 * which always scrolls to the bottom.
 *
 * The flag records the reader's last intent as expressed by a scroll gesture;
 * it is deliberately not recomputed on resize/reflow. Recomputing from
 * geometry would detach a following reader whenever a reflow (e.g. a drawer
 * resize re-wrapping the text) transiently moves the bottom away.
 *
 * Scrolls the container directly instead of using `scrollIntoView`:
 * `scrollIntoView` on an element inside a `position: fixed` drawer scrolls the
 * document (resetting page scroll to 0) because the browser resolves the
 * element's position relative to the document, not the viewport.
 *
 * @returns `containerRef` for the scroll container and `onScroll` for its
 *   `onScroll` handler.
 */
const useChatAutoScroll = ({
  messages,
  streamingText,
  threshold = 150
}: UseChatAutoScrollOptions) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const pinnedToBottomRef = useRef(true)

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [])

  const onScroll = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    pinnedToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold
  }, [threshold])

  useEffect(() => {
    const userJustSent = messages[messages.length - 1]?.sender === 'user'
    if (userJustSent || pinnedToBottomRef.current) scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (streamingText && pinnedToBottomRef.current) scrollToBottom()
  }, [streamingText, scrollToBottom])

  return { containerRef, onScroll }
}

export default useChatAutoScroll
